import { useState, useEffect, useRef, useCallback } from 'react';
import { useSessionServices } from '../context/SessionServicesContext';
import i18n from 'i18next';
import { logger } from '../utils/logger';

interface UseTranscriptValidationParams {
  enabled: boolean;
  sessionStatus: 'loading' | 'ready' | 'active' | 'finished';
  messages: Array<{ speaker: 'user' | 'ai'; text: string; timestamp: number }>;
  scenarioId?: string;
  scenarioTitle?: string;
  initialTranscriptLength?: number;
}

interface UseTranscriptValidationReturn {
  isTranscriptInvalid: boolean;
  invalidReason: string;
  resetInvalid: () => void;
  isConversationEnding: boolean;
  farewellSignals: string;
}

export function useTranscriptValidation({
  enabled,
  sessionStatus,
  messages,
  scenarioId,
  scenarioTitle,
  initialTranscriptLength = 0,
}: UseTranscriptValidationParams): UseTranscriptValidationReturn {
  const { validateTranscript } = useSessionServices();

  const [isTranscriptInvalid, setIsTranscriptInvalid] = useState(false);
  const [invalidReason, setInvalidReason] = useState('');
  const [isConversationEnding, setIsConversationEnding] = useState(false);
  const [farewellSignals, setFarewellSignals] = useState('');
  
  // Track the last message count we validated.
  // When restoring a saved session, skip the restored exchanges so we don't
  // immediately trigger farewell detection on the old transcript.
  const lastValidatedCountRef = useRef(initialTranscriptLength);
  const isValidatingRef = useRef(false);
  const glowTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Validate transcript every 2 exchanges (1 exchange = 1 user message + 1 AI message)
   */
  const checkTranscriptValidity = useCallback(async () => {
    if (!enabled) {
      logger.debug('[TranscriptValidation] 🔴 BLOCKED: Validation disabled');
      return;
    }

    if (sessionStatus !== 'active') {
      logger.debug('[TranscriptValidation] 🔴 BLOCKED: Session not active');
      return;
    }

    if (!scenarioId || !scenarioTitle) {
      logger.debug('[TranscriptValidation] 🔴 BLOCKED: Missing scenario data');
      return;
    }

    if (isValidatingRef.current) {
      logger.debug('[TranscriptValidation] 🔴 BLOCKED: Already validating');
      return;
    }

    // Check if we have at least 2 new exchanges since last validation
    // An exchange = 1 user message + 1 AI message
    const totalExchanges = Math.min(
      messages.filter(m => m.speaker === 'user').length,
      messages.filter(m => m.speaker === 'ai').length
    );
    
    // 🛡️ MINIMUM THRESHOLD: Only start validating after 4 complete exchanges
    // This prevents false positives from early noise transcription
    const MIN_EXCHANGES_BEFORE_VALIDATION = 4;
    
    if (totalExchanges < MIN_EXCHANGES_BEFORE_VALIDATION) {
      logger.debug('[TranscriptValidation] 🔴 BLOCKED: Need minimum exchanges before validation', {
        totalExchanges,
        minimumRequired: MIN_EXCHANGES_BEFORE_VALIDATION,
        needMore: MIN_EXCHANGES_BEFORE_VALIDATION - totalExchanges
      });
      return;
    }

    const exchangesSinceLastValidation = totalExchanges - lastValidatedCountRef.current;

    if (exchangesSinceLastValidation < 2) {
      logger.debug('[TranscriptValidation] 🔴 BLOCKED: Not enough new exchanges', {
        totalExchanges,
        lastValidated: lastValidatedCountRef.current,
        exchangesSinceLastValidation,
        needMore: 2 - exchangesSinceLastValidation
      });
      return;
    }

    logger.debug('[TranscriptValidation] ✅ Checking transcript validity...', {
      totalExchanges,
      lastValidated: lastValidatedCountRef.current,
      exchangesSinceLastValidation
    });

    isValidatingRef.current = true;

    try {
      // Get last 10 exchanges (10 user + 10 AI messages = 20 messages total)
      // Larger window allows percentage-based validation instead of just counting errors
      const userMessages = messages.filter(m => m.speaker === 'user');
      const aiMessages = messages.filter(m => m.speaker === 'ai');
      
      // Get last 10 exchanges
      const recentUserMessages = userMessages.slice(-10);
      const recentAiMessages = aiMessages.slice(-10);
      
      // Interleave messages in chronological order
      const recentExchanges: Array<{ speaker: string; message: string }> = [];
      const maxLength = Math.max(recentUserMessages.length, recentAiMessages.length);
      
      for (let i = 0; i < maxLength; i++) {
        if (i < recentUserMessages.length) {
          recentExchanges.push({
            speaker: 'user',
            message: recentUserMessages[i].text
          });
        }
        if (i < recentAiMessages.length) {
          recentExchanges.push({
            speaker: 'avatar',
            message: recentAiMessages[i].text
          });
        }
      }

      // Sort by timestamp to ensure correct order
      const messagesWithTimestamps = messages.slice(-20); // Last 20 messages total
      const sortedRecentExchanges = messagesWithTimestamps.map(m => ({
        speaker: m.speaker === 'user' ? 'user' : 'avatar',
        message: m.text
      }));

      logger.debug('[TranscriptValidation] 📝 Sending for validation:', {
        exchangeCount: Math.floor(sortedRecentExchanges.length / 2),
        messageCount: sortedRecentExchanges.length
      });

      // Get current language
      const currentLanguage = i18n.language || 'en';

      const result = await validateTranscript(
        scenarioId,
        scenarioTitle,
        sortedRecentExchanges,
        currentLanguage
      );

      logger.debug('[TranscriptValidation] 📊 Validation result:', {
        valid: result.valid,
        hasReason: Boolean(result.reason),
        conversationEnding: result.conversationEnding
      });

      if (!result.valid) {
        logger.info('[TranscriptValidation] 🚨 INVALID TRANSCRIPT DETECTED!');
        setIsTranscriptInvalid(true);
        setInvalidReason(result.reason);
      } else {
        logger.debug('[TranscriptValidation] ✅ Transcript is valid');
      }

      // Check for conversation ending signals
      logger.debug('[FAREWELL_DETECTION] conversationEnding flag:', result.conversationEnding);
      logger.debug('[FAREWELL_DETECTION] farewellSignals present:', Boolean(result.farewellSignals));
      
      if (result.conversationEnding === true) {
        logger.info('[FAREWELL_DETECTION] 👋 ✅ ENDING DETECTED!');
        logger.debug('[FAREWELL_DETECTION] 🌟 Setting isConversationEnding = TRUE');
        setIsConversationEnding(true);
        setFarewellSignals(result.farewellSignals || '');
      } else {
        logger.debug('[FAREWELL_DETECTION] 👍 Conversation ongoing', result.conversationEnding);
        // Reset ending state if no longer detected
        setIsConversationEnding(false);
        setFarewellSignals('');
      }

      // Update last validated count
      lastValidatedCountRef.current = totalExchanges;

    } catch (error) {
      console.error('[TranscriptValidation] ❌ Error during validation:', error);
      // Don't interrupt session on validation error
    } finally {
      isValidatingRef.current = false;
    }
  }, [enabled, sessionStatus, messages, scenarioId, scenarioTitle]);

  /**
   * Auto-reset conversation ending glow after 15 seconds
   * This prevents the glow effect from staying forever if it was a false positive
   */
  useEffect(() => {
    if (isConversationEnding) {
      logger.debug('[FAREWELL_DETECTION] ⏰ Starting 15-second glow timeout');
      
      // Clear any existing timeout
      if (glowTimeoutRef.current) {
        clearTimeout(glowTimeoutRef.current);
      }
      
      // Set new timeout to reset glow after 15 seconds
      glowTimeoutRef.current = setTimeout(() => {
        logger.debug('[FAREWELL_DETECTION] ⏰ 15 seconds elapsed - resetting glow effect');
        setIsConversationEnding(false);
        setFarewellSignals('');
        glowTimeoutRef.current = null;
      }, 15000); // 15 seconds
      
      return () => {
        if (glowTimeoutRef.current) {
          clearTimeout(glowTimeoutRef.current);
          glowTimeoutRef.current = null;
        }
      };
    }
  }, [isConversationEnding]);

  /**
   * Check transcript validity when new messages arrive
   */
  useEffect(() => {
    if (messages.length === 0) return;

    // Debounce validation check slightly to avoid multiple rapid calls
    const timeoutId = setTimeout(() => {
      checkTranscriptValidity();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [messages.length, checkTranscriptValidity]);

  /**
   * Reset invalid state (called when user restarts)
   */
  const resetInvalid = useCallback(() => {
    setIsTranscriptInvalid(false);
    setInvalidReason('');
    setIsConversationEnding(false);
    setFarewellSignals('');
    lastValidatedCountRef.current = 0;
  }, []);

  return {
    isTranscriptInvalid,
    invalidReason,
    resetInvalid,
    isConversationEnding,
    farewellSignals
  };
}


